import { Injectable } from '@angular/core';
import { ItemMinimalDetails, ProductItem } from '../models/cart-data.model';
import { CheckoutData } from '../models/checkout-data.model';
import { UserProfile } from '../models/auth-data.model';
import { AuthService } from './auth.service';
import { Observable, ReplaySubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from './app-config.service';
import { ToastService } from 'ng-uikit-pro-standard';
import { AppConfig } from '../models/app-config';

declare global {
    interface Window {
        klaviyo: any;
    }
}

// NOTE: All numbers are put inside strings since they could have decimal places
// And we want it sent to Klaviyo as e.g. 32.20 and not 32.199999999999999999999

@Injectable({
    providedIn: 'root'
})
export class KlaviyoService {
    public readonly imageRoot = 'https://img.vistek.net';
    private _user: UserProfile = null;
    private _isInit = false;
    private _initSubject = new ReplaySubject<boolean>();
    private _vistekUrl: string;
    private _klaviyoFormSettings: AppConfig['KlaviyoFormsSettings'];
    private _klaviyoSubscriptionListSettings: AppConfig['KlaviyoSubscriptionListSettings'];

    public get init$(): Observable<boolean> {
        return this._initSubject;
    }

    constructor(
        private _authService: AuthService, 
        private _httpClient: HttpClient, 
        private _appConfigService: AppConfigService,
        private _toaster: ToastService
    ) {
        this._vistekUrl = this._appConfigService.vistekUrl;
        this._klaviyoFormSettings = this._appConfigService.klaviyoFormsSettings;
        this._klaviyoSubscriptionListSettings = this._appConfigService.klaviyoSubscriptionListSettings;

        window.addEventListener('klaviyoForms', (e: any) => {
            try {
                if (e.detail.type == 'submit') {
                    this.onSubmitForm(e);
                }
            }
            catch {
            }
        });

        this._authService.UserProfileSubject.subscribe((user) => {
            this._user = user;
            this.identifyUser();

            if (!this._isInit) {
                this._isInit = true;
                this._initSubject.next(true);
            }
        });

        this._authService.getUserProfile().subscribe();
    }

    public viewedPage() {
        const payload = {
            pageUrl: window.location.href,
            pageTitle: document.title
        };

        window.klaviyo.push(['track', 'Viewed Page', payload]);
    }

    public viewedProduct(item: ItemMinimalDetails): void {
        try {
            const viewedProduct = this.mapProductFromItemMinimalDetails(item);
            const recentlyViewedItem = this.mapRecentlyViewedItem(item);

            window.klaviyo.push(['track', 'Viewed Product', viewedProduct]);
            window.klaviyo.push(['trackViewedItem', recentlyViewedItem]);
        } catch (ex) {
            console.error('Klaviyo Viewed Product failed', ex, item);
        }
    }

    public startedCheckout(data: CheckoutData): void {
        try {
            this.identifyUser(data.CartOrder.Email, data.CartOrder.FirstName, data.CartOrder.LastName)
                .then((x) => {
                    const payload = this.mapStartedCheckout(data);
                    window.klaviyo.push(['track', 'Started Checkout', payload]);
                })
                .catch((ex) => {
                    console.error('Klaviyo Started Checkout failed', ex, data);
                });
        } catch (ex) {
            console.error('Klaviyo Started Checkout failed', ex, data);
        }
    }

    private identifyUser(email: string = null, firstName: string = null, lastName: string = null): Promise<any> {
        try {
            if (!this._user && !email) {
                // Try to identify by saved cookie.
                return window.klaviyo.identify({});
            }

            let klaviyoUser = {
                email: email,
                first_name: firstName,
                last_name: lastName
            };

            if (this._user) {
                klaviyoUser.email = this._user.Email;
                klaviyoUser['first_name'] = this._user.FirstName;
                klaviyoUser['last_name'] = this._user.LastName;
            }

            return window.klaviyo.identify(klaviyoUser);
        } catch (ex) {
            console.error('Failed to identify Klaviyo user.', ex);
            return Promise.resolve();
        }
    }

    private mapStartedCheckout(data: CheckoutData) {
        let itemNames = [];
        let categories = [];

        let items = data.UnBundledItems.map((x) => {
            const mappedItem = this.mapProductFromCartItem(x);
            itemNames.push(mappedItem.name);
            categories.push(...mappedItem.categories);

            return mappedItem;
        });

        const bundles = [];

        data.BundleSets.forEach((b) => {
            let bundle = {
                bundleTotal: `${b.Total}`,
                bundleSavings: `${b.TotalSavings}`,
                bundleItems: []
            };

            bundle.bundleItems = b.Items.map((x) => {
                const mappedItem = this.mapProductFromCartItem(x);
                itemNames.push(mappedItem.name);
                categories.push(...mappedItem.categories);

                return mappedItem;
            });

            bundles.push(bundle);
        });

        itemNames = Array.from(new Set(itemNames));
        categories = Array.from(new Set(categories));

        const payload = {
            $event_id: `${data.CartID}T${Math.floor(Date.now() / 1000)}`,
            $value: `${data.CartSummary.GrandTotal}`,
            itemNames,
            categories,
            checkoutUrl: `${window.location.host}/cart/checkout`,
            items,
            bundles
        };

        return payload;
    }

    private mapProductFromItemMinimalDetails(item: ItemMinimalDetails): any {
        return {
            name: `${item.Manufacturer?.trim() ?? ''} ${item.Description.trim()}`,
            webCode: item.Webcode,
            categories: item.Categories,
            imageUrl: this.imageRoot + item.ImageHtml?.Src ?? '/prodimg/200/blank.jpg',
            productUrl: item.AbsoluteUrl,
            manufacturer: item.Manufacturer,
            unitPrice: `${item.SellingPrice}`
        };
    }

    private mapProductFromCartItem(item: ProductItem): any {
        const categories = [item.CategoryLevel1, item.CategoryLevel2, item.CategoryLevel3, item.CategoryLevel4].filter(
            (x) => x
        );

        const discount = Math.abs(item.DiscountTotal);
        const productUrl = window.location.host + item.ItemUrl;
        let imageUrl = this.imageRoot + '/prodimg/200/blank.jpg';

        if (item.ImageDataburst?.length) {
            imageUrl = this.imageRoot + item.ImageDataburst[0];
        }

        return {
            name: `${item.Manufacturer?.trim() ?? ''} ${item.Description.trim()}`,
            webCode: item.Webcode,
            categories,
            imageUrl,
            productUrl,
            manufacturer: item.Manufacturer,
            unitPrice: `${item.UnitPrice}`,
            totalDiscount: `${discount * -1}`,
            totalPrice: `${item.UnitPrice * item.Quantity - discount}`,
            quantity: item.Quantity
        };
    }

    // This event requires us to follow an exact schema.
    private mapRecentlyViewedItem(item: ItemMinimalDetails): any {
        return {
            Title: `${item.Manufacturer.trim()} ${item.Description.trim()}`,
            ItemId: item.Webcode,
            Categories: item.Categories,
            ImageUrl: this.imageRoot + item.ImageHtml?.Src ?? '/prodimg/200/blank.jpg',
            Url: item.AbsoluteUrl,
            Metadata: {
                Brand: item.Manufacturer,
                Price: `${item.SellingPrice}`
            }
        };
    }

    private onSubmitForm(e: any): void {
        const email = e.detail.metaData.$email;
        const formId = e.detail.formId;

        try {
            window.klaviyo.identify({
                email
            });
    
            if (formId === this._klaviyoFormSettings.BackInStockFormId) {
                this.pushSignedUpBackInStock(email);
            }
            else if (formId === this._klaviyoFormSettings.ContestSignUpFormId) {
                this.pushSignedUpForContest(email);
            }
            else {
                this._toaster.success('Successfully submitted the form.');
            }
        }
        catch {
            console.error(`Klaviyo onSubmitForm failed. Form ID: ${formId}. Email: ${email}`);
            this._toaster.error('An unknown error occured with submission of your form.');
        }
    }

    private pushSignedUpBackInStock(email: any) {
        if (!email) {
            throw 'pushSignedUpBackInStock: Email is required but not supplied';
        }

        const url = window.location.href;
        const expression = /\/store\/([0-9]+)\//;
        const match = url.match(expression);
        if (match.length !== 2) {
            return;
        }

        const webCode = match[1];

        const payload = {
            WebCode: webCode,
            Email: email
        };

        this._httpClient
            .post(`${this._vistekUrl}/Marketing/SubscribeToBackInStock`, payload)
            .subscribe(
                () => this._toaster.success('Successfully subscribed to back in stock notifications.'),
                () => this._toaster.error('Failed to subscribe to back in stock notifications. Please try again.')
            );
    }

    private pushSignedUpForContest(email: any) {
        if (!email) {
            throw 'pushSignedUpForContest: Email is required but not supplied';
        }

        const contestName = (document.querySelector(`input[name="form-${this._klaviyoFormSettings.ContestSignUpFormId}"]`) as HTMLInputElement).value;

        const payload = {
            ContestName: contestName,
            Email: email
        };

        this._httpClient
            .post(`${this._vistekUrl}/Marketing/SignUpForContest`, payload)
            .subscribe(
                () => this._toaster.success('Successfully signed up to the contest. Good luck!'),
                () => this._toaster.error('Failed to sign up to the contest. Please try again.')
            );
    }
}
